import type { AxiosRequestConfig } from "axios"

import { caseConverterClient, originalCaseClient } from "@/api/client"
import type { ApiResultPromise } from "@/api/errors"
import { apiResult } from "@/api/utils"

type DataPromise<T> = Promise<{ data: T }>

type GetClient = <T>(url: string, config?: AxiosRequestConfig) => DataPromise<T>
type DeleteClient = <T>(url: string, config?: AxiosRequestConfig) => DataPromise<T>
type BodyClient = <T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
) => DataPromise<T>
type FormClient = <T>(
  url: string,
  data: FormData,
  config?: AxiosRequestConfig,
) => DataPromise<T>

interface RawApiClient {
  get: GetClient
  post: BodyClient
  put: BodyClient
  patch: BodyClient
  delete: DeleteClient
  postForm: FormClient
  putForm: FormClient
}

function makeBodyCall<T>(
  rawCall: BodyClient,
  url: string,
  data: unknown,
  config: AxiosRequestConfig | undefined,
  includeData: boolean,
): DataPromise<T> {
  if (!includeData) {
    return config === undefined ? rawCall<T>(url) : rawCall<T>(url, undefined, config)
  }
  return config === undefined ? rawCall<T>(url, data) : rawCall<T>(url, data, config)
}

function createApiClient(client: RawApiClient) {
  return {
    get<T>(
      url: string,
      fallbackMessage: string,
      config?: AxiosRequestConfig,
    ): ApiResultPromise<T> {
      return apiResult(
        () => (config === undefined ? client.get<T>(url) : client.get<T>(url, config)),
        fallbackMessage,
      )
    },

    post<T>(
      url: string,
      fallbackMessage: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): ApiResultPromise<T> {
      return apiResult(
        () => makeBodyCall<T>(client.post, url, data, config, arguments.length > 2),
        fallbackMessage,
      )
    },

    put<T>(
      url: string,
      fallbackMessage: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): ApiResultPromise<T> {
      return apiResult(
        () => makeBodyCall<T>(client.put, url, data, config, arguments.length > 2),
        fallbackMessage,
      )
    },

    patch<T>(
      url: string,
      fallbackMessage: string,
      data?: unknown,
      config?: AxiosRequestConfig,
    ): ApiResultPromise<T> {
      return apiResult(
        () => makeBodyCall<T>(client.patch, url, data, config, arguments.length > 2),
        fallbackMessage,
      )
    },

    delete<T>(
      url: string,
      fallbackMessage: string,
      config?: AxiosRequestConfig,
    ): ApiResultPromise<T> {
      return apiResult(
        () =>
          config === undefined ? client.delete<T>(url) : client.delete<T>(url, config),
        fallbackMessage,
      )
    },

    postForm<T>(
      url: string,
      fallbackMessage: string,
      data: FormData,
      config?: AxiosRequestConfig,
    ): ApiResultPromise<T> {
      return apiResult(
        () =>
          config === undefined
            ? client.postForm<T>(url, data)
            : client.postForm<T>(url, data, config),
        fallbackMessage,
      )
    },

    putForm<T>(
      url: string,
      fallbackMessage: string,
      data: FormData,
      config?: AxiosRequestConfig,
    ): ApiResultPromise<T> {
      return apiResult(
        () =>
          config === undefined
            ? client.putForm<T>(url, data)
            : client.putForm<T>(url, data, config),
        fallbackMessage,
      )
    },
  }
}

export const apiClient = createApiClient(caseConverterClient)
export const originalCaseApiClient = createApiClient(originalCaseClient)
